import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, ScanLine } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { http, apiError, money } from "@/lib/shop";
import { ScannerDialog } from "./parts/ScannerDialog";

export const UsePartDialog = ({ repair, onClose, onDone }) => {
  const [q, setQ] = useState("");
  const [parts, setParts] = useState([]);
  const [picked, setPicked] = useState(null);
  const [qty, setQty] = useState("1");
  const [scan, setScan] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (repair) { setQ(repair.device || ""); setPicked(null); setQty("1"); } }, [repair]);

  useEffect(() => {
    if (!repair) return undefined;
    const t = setTimeout(() => {
      http.get("/parts", { params: q.trim() ? { q: q.trim() } : {} }).then((r) => setParts(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q, repair]);

  const onScanned = async (code) => {
    try { setPicked((await http.get(`/parts/by-code/${encodeURIComponent(code)}`)).data); } catch { toast.error("No part with that code"); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await http.post(`/repairs/${repair._id}/parts`, { part_id: picked.id, quantity: parseInt(qty, 10) });
      toast.success(`${picked.name} marked as used — stock reduced`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!repair} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="use-part-dialog">
        <DialogHeader>
          <DialogTitle>Use a part in this repair</DialogTitle>
          <DialogDescription>{repair?.name} · {repair?.device} · {repair?.service}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search parts by model" data-testid="use-part-search" />
          </div>
          <button type="button" onClick={() => setScan(true)} className="inline-flex items-center gap-1 rounded-md border px-3 text-sm font-semibold hover:border-brand" data-testid="use-part-scan-btn"><ScanLine className="h-4 w-4" /> Scan</button>
        </div>
        <div className="grid gap-2 max-h-64 overflow-y-auto">
          {!parts.length && <p className="text-sm text-slate-500">No matching parts.</p>}
          {parts.map((p) => (
            <button key={p.id} onClick={() => setPicked(p)} disabled={p.quantity < 1} className={`text-left rounded-lg border p-3 text-sm disabled:opacity-50 ${picked?.id === p.id ? "border-brand bg-brand-50" : "border-slate-200 hover:border-brand"}`} data-testid={`use-part-option-${p.id}`}>
              <span className="font-bold">{p.name}</span> <span className="text-slate-500">· {p.compatible_model || p.category}</span>
              <span className="float-right font-semibold">{p.quantity} in stock · {money(p.selling_price)}</span>
            </button>
          ))}
        </div>
        {picked && (
          <div className="flex items-end gap-3 rounded-xl bg-slate-50 p-3">
            <div className="flex-1 text-sm"><p className="font-bold">{picked.name}</p><p className="text-slate-500">{picked.quantity} in stock</p></div>
            <Input type="number" min="1" max={picked.quantity} className="w-20" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="use-part-qty" />
            <button onClick={submit} disabled={busy} className="rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-700 disabled:opacity-60" data-testid="use-part-confirm-btn">Mark as used</button>
          </div>
        )}
        <ScannerDialog open={scan} onOpenChange={setScan} onCode={onScanned} />
      </DialogContent>
    </Dialog>
  );
};
